import type { MouseEvent } from 'react';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import {
  Icon,
  PageFrame,
  PageHeader,
  Section,
  StatusChip,
  WS_COLOR,
  WS_RADIUS,
  WS_STATUS,
  WorkspaceThemeProvider,
  icons,
  type IconProps,
} from '@platform/ui-kit/workspace';

/**
 * User Guide — AI Readiness, as a Decision Workspace screen.
 *
 * Shown with `?ui=workspace` next to the ported original in `UserGuide.tsx`, which stays the
 * default until the owner approves this one (CLAUDE.md 1.2). The original's header comment
 * explains where each section's words come from; none are changed here, and the platform-fit
 * sections are still WRITTEN rather than cloned (the source guide has no entry for them).
 *
 * WHAT IS UNCHANGED: every word of every section, the section order, the "v2.0" badge, the
 * quick-navigation list and what each link goes to.
 *
 * WHAT CHANGES, by the design system and nothing else: the page frame (locked header, the guide
 * is the one scroll region), flat sections in place of stacked cards, Lucide icons in place of
 * emoji and Material Icons, and the contents list as links that scroll the guide.
 */

type Glyph = IconProps['icon'];

interface FeatureCard {
  icon: Glyph;
  title: string;
  body: string;
  points?: string[];
}

interface GuideSection {
  id: string;
  icon: Glyph;
  label: string;
  heading: string;
  intro?: string;
  features?: FeatureCard[];
  steps?: { n: string; title: string; desc: string }[];
}

/* Copied from UserGuide.tsx: the original's data is not exported, and editing a ported file to
   export it is not allowed. When this screen is approved the original goes, and so does the copy. */
const SECTIONS: GuideSection[] = [
  {
    id: 'lifecycle',
    icon: icons.Rocket,
    label: 'The five steps',
    heading: 'How AI Readiness works',
    intro:
      'Score every initiative against one consistent framework, weigh its benefit and its ' +
      'cost, sequence what survives into a roadmap, and run delivery. Each step below has its ' +
      'own screens, reachable from the sidebar or the Start Menu.',
    steps: [
      {
        n: '1',
        title: 'Score — objectively',
        desc: 'One framework for every initiative, so priority reflects evidence rather than the loudest voice.',
      },
      {
        n: '2',
        title: 'Weigh the benefit',
        desc: 'Put a number on savings, ROI and value realised over time.',
      },
      {
        n: '3',
        title: 'Weigh the cost',
        desc: 'Model build and run cost, then set benefit against cost to decide.',
      },
      {
        n: '4',
        title: 'Shape the roadmap',
        desc: 'Sequence the chosen work into phases, dependencies and timelines.',
      },
      {
        n: '5',
        title: 'Run delivery',
        desc: 'Define the programs, lock the timeline, then track progress against plan.',
      },
    ],
  },
  {
    id: 'strategic-intelligence',
    icon: icons.BrainCircuit,
    label: 'Strategic Intelligence',
    heading: 'Strategic Intelligence & Discovery',
    intro:
      'Strategic Intelligence is where initiatives originate. Analyze companies, generate ' +
      'candidate AI opportunities, and define the assessment criteria that drive scoring. ' +
      'Available to Administrators and Strategic Analysts.',
    features: [
      {
        icon: icons.Telescope,
        title: 'Strategic Discoveries',
        body:
          'Run AI-assisted discovery against a company to surface candidate initiatives, use ' +
          'cases, and value hypotheses.',
        points: [
          'Capture the company profile and strategic context',
          'Generate candidate opportunities from discovery sessions',
          'Promote discoveries into managed opportunities',
        ],
      },
      {
        icon: icons.SlidersHorizontal,
        title: 'Scoring Frameworks',
        body:
          'Define the dimensions, questions, and weights that determine how every opportunity ' +
          'is evaluated.',
        points: [
          'Configure dimensions and sub-criteria',
          'Manage the question & answer catalog',
          'Tune weights to reflect strategy',
        ],
      },
    ],
  },
  {
    id: 'platform-fit',
    icon: icons.Compass,
    label: 'AI Platform Fit',
    heading: 'Choosing the AI platform',
    intro: 'These screens decide WHICH AI platform an opportunity should be delivered on, and why.',
    features: [
      {
        icon: icons.Package,
        title: 'AI Platforms Catalog',
        body:
          'The catalogue of AI platforms under consideration, with the profile held for each ' +
          'one. Everything downstream scores against this list, so a platform has to exist ' +
          'here before it can be compared.',
      },
      {
        icon: icons.Sparkles,
        title: 'Platform Analysis',
        body: 'AI-assisted analysis of a platform, building the profile the fitment scoring reads.',
      },
      {
        icon: icons.PlayCircle,
        title: 'Program Quick Start',
        body:
          'Upload a client plan — a deck, a workbook, a report, a screenshot — and the analysis ' +
          'proposes the projects, opportunities, dates, work items and RACI it can read from ' +
          'it. Nothing is created until you press Generate; the screen only builds a preview ' +
          'you select from, and it cannot invent records that are not in the file.',
      },
      {
        icon: icons.ListChecks,
        title: 'Platform Fitment Criteria',
        body:
          'The criteria and weights that decide how well a platform fits an opportunity — the ' +
          'equivalent of a scoring framework, for platforms rather than initiatives.',
      },
      {
        icon: icons.BarChart3,
        title: 'AI Platform Fit',
        body: 'Scores each opportunity against each platform using those criteria.',
      },
      {
        icon: icons.TrendingUp,
        title: 'Platform Fit Summary',
        body: 'The portfolio-level view of those results — which platform suits which work.',
      },
    ],
  },
  {
    id: 'settings',
    icon: icons.Settings,
    label: 'Settings',
    heading: 'Settings',
    features: [
      {
        icon: icons.SlidersHorizontal,
        title: 'AI Platform Selection',
        body:
          'Holds the selection criteria and the fit results behind the Strategic Intelligence ' +
          'screens. Change the criteria here and the fit scores are recalculated against them.',
      },
    ],
  },
];

export function UserGuideWorkspace() {
  return (
    <WorkspaceThemeProvider baseline={false}>
      <Screen />
    </WorkspaceThemeProvider>
  );
}

function Screen() {
  const go = (id: string) => (e: MouseEvent) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ block: 'start' });
  };

  return (
    <PageFrame
      header={
        <PageHeader
          title="User Guide"
          meta={<StatusChip tone="neutral" label="v2.0" />}
          subtitle="Complete guide to assessing AI readiness and platform fit"
        />
      }
    >
      <Box tabIndex={0} sx={{ flex: 1, minWidth: 0, minHeight: 0, overflowY: 'auto', pb: 6 }}>
        <Box sx={{ maxWidth: 960 }}>
          <Box component="nav" aria-label="Quick Navigation" sx={{ mb: 4 }}>
            <Typography variant="h2" component="h2" sx={{ mb: 2 }}>
              Quick Navigation
            </Typography>
            <Box
              component="ul"
              sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexWrap: 'wrap', gap: 3 }}
            >
              {SECTIONS.map((s) => (
                <li key={s.id}>
                  <Link
                    href={`#${s.id}`}
                    onClick={go(s.id)}
                    underline="hover"
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5, minHeight: 36 }}
                  >
                    <Icon icon={s.icon} size={16} />
                    {s.label}
                  </Link>
                </li>
              ))}
            </Box>
          </Box>

          {SECTIONS.map((s) => (
            <Section key={s.id} id={s.id} title={s.heading} description={s.intro}>
              {s.steps ? (
                <Box
                  component="ol"
                  sx={{
                    listStyle: 'none',
                    m: 0,
                    p: 0,
                    display: 'grid',
                    gap: 3,
                    gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
                  }}
                >
                  {s.steps.map((q) => (
                    <Box
                      component="li"
                      key={q.n}
                      sx={{
                        p: 3,
                        border: `1px solid ${WS_COLOR.border}`,
                        borderRadius: `${WS_RADIUS.card}px`,
                      }}
                    >
                      <Box
                        aria-hidden
                        sx={{
                          width: 28,
                          height: 28,
                          display: 'grid',
                          placeItems: 'center',
                          borderRadius: '50%',
                          fontWeight: 700,
                          mb: 1.5,
                          backgroundColor: WS_STATUS.info.fill,
                          color: WS_STATUS.info.ink,
                        }}
                      >
                        {q.n}
                      </Box>
                      <Typography sx={{ fontWeight: 600 }}>{q.title}</Typography>
                      <Typography variant="body2" sx={{ color: WS_COLOR.textMuted }}>
                        {q.desc}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : null}

              {s.features ? (
                <Box
                  sx={{
                    display: 'grid',
                    gap: 4,
                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                  }}
                >
                  {s.features.map((f) => (
                    <Box
                      key={f.title}
                      sx={{
                        p: 4,
                        border: `1px solid ${WS_COLOR.border}`,
                        borderRadius: `${WS_RADIUS.card}px`,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                        <Icon icon={f.icon} />
                        <Typography variant="h3" component="h3">
                          {f.title}
                        </Typography>
                      </Box>
                      <Typography variant="body2">{f.body}</Typography>
                      {f.points ? (
                        <Box
                          component="ul"
                          sx={{ listStyle: 'none', m: 0, mt: 2, p: 0, display: 'grid', gap: 1 }}
                        >
                          {f.points.map((p) => (
                            <Box
                              component="li"
                              key={p}
                              sx={{
                                display: 'flex',
                                gap: 2,
                                alignItems: 'flex-start',
                                color: WS_STATUS.success.ink,
                              }}
                            >
                              <Box sx={{ mt: '2px', flex: 'none' }}>
                                <Icon icon={icons.CheckCircle2} size={16} />
                              </Box>
                              <Typography variant="body2" sx={{ color: WS_COLOR.text }}>
                                {p}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      ) : null}
                    </Box>
                  ))}
                </Box>
              ) : null}
            </Section>
          ))}
        </Box>
      </Box>
    </PageFrame>
  );
}
